"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { playLessonAudioBuffer, stopLessonAudio } from "@lesson/shared/lessonAudio";
import { stripEndToken } from "@lib/agent/conversation/endToken";
import { languageNameForLocale, sttLanguageCode } from "@lib/languageNames";
import { fetchTutorSpeechBuffer } from "@lib/voice/fetchTutorSpeech";
import { useAudioRecorder } from "@lib/voice/useAudioRecorder";
import { useLessonPreferences } from "@components/lesson/LessonPreferencesProvider";
import { useTranslations } from "@i18n/provider";
import type { OverlayMessage } from "../types";
import { ConversationMicButton } from "./ConversationMicButton";
import { SendButton } from "@ui/SendButton";

type ConversationInputProps = {
  messages: OverlayMessage[];
  targetLocale: string;
  nativeLocale: string;
  isStreaming: boolean;
  voiceFallback: boolean;
  onVoiceFallback: () => void;
  onSend: (text: string) => void;
  disabled?: boolean;
};

export function ConversationInput({
  messages,
  targetLocale,
  nativeLocale,
  isStreaming,
  voiceFallback,
  onVoiceFallback,
  onSend,
  disabled,
}: ConversationInputProps) {
  const { t } = useTranslations();
  const { maxRecordingSeconds } = useLessonPreferences();
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [localBannerShown, setLocalBannerShown] = useState(false);
  const bannerShown = voiceFallback || localBannerShown;
  const lastTtsMessageIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Dismissing the widget mid-playback must also silence the tutor.
  const targetLanguage = languageNameForLocale(targetLocale, nativeLocale);

  const triggerVoiceFallback = useCallback(() => {
    if (!bannerShown) {
      setLocalBannerShown(true);
      onVoiceFallback();
    }
  }, [bannerShown, onVoiceFallback]);

  useEffect(() => {
    const lastPersona = [...messages].reverse().find((m) => m.role === "persona" && !m.isStreaming);
    if (!lastPersona || lastPersona.id === lastTtsMessageIdRef.current) return;
    if (!lastPersona.text.trim()) return;

    lastTtsMessageIdRef.current = lastPersona.id;
    const { display } = stripEndToken(lastPersona.text);
    if (!display.trim()) return;

    void (async () => {
      const buffer = await fetchTutorSpeechBuffer(display, { targetLocale });
      if (!buffer) {
        triggerVoiceFallback();
        return;
      }
      try {
        await playLessonAudioBuffer(buffer);
      } catch {
        triggerVoiceFallback();
      }
    })();
  }, [messages, targetLocale, triggerVoiceFallback]);

  const { isRecording, startRecording, stopRecording } = useAudioRecorder({
    maxRecordingSeconds,
    onRecordingComplete: handleRecordingComplete,
    onError: () => {
      setMicError(t("speaking.errors.microphoneUnavailable"));
      triggerVoiceFallback();
    },
    onTeardown: stopLessonAudio,
  });

  /** The chip guards on transport state before touching the mic. */
  function beginRecording() {
    if (disabled || isStreaming || isProcessing) return;
    setMicError(null);
    void startRecording();
  }

  async function handleRecordingComplete(blob: Blob) {
    setIsProcessing(true);
    setMicError(null);
    try {
      const file = new File([blob], "recording.webm", { type: blob.type });
      const fd = new FormData();
      fd.append("audio", file);
      fd.append("language", sttLanguageCode(targetLocale));
      const sttRes = await fetch("/api/voice/transcribe", { method: "POST", body: fd });
      if (!sttRes.ok) {
        setMicError(t("speaking.errors.transcribeFailed"));
        triggerVoiceFallback();
        return;
      }
      const { text } = (await sttRes.json()) as { text: string };
      const trimmed = text.trim();
      if (trimmed) {
        onSend(trimmed);
        setInput("");
      }
    } finally {
      setIsProcessing(false);
    }
  }

  function handleSend() {
    const text = input.trim();
    if (!text || isStreaming || disabled) return;
    onSend(text);
    setInput("");
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const inputDisabled = disabled || isStreaming;

  return (
    <div
      className="flex-shrink-0 px-3 pb-3 pt-2"
      style={{ borderTop: "1px solid var(--hairline)" }}
    >
      {bannerShown ? (
        <p className="mb-2 text-xs text-writing-fg" role="alert">
          {t("agent.conversation.voiceFallback")}
        </p>
      ) : null}
      {micError ? (
        <p className="mb-2 text-xs text-writing-fg" role="alert">
          {micError}
        </p>
      ) : null}
      <div className="flex items-end gap-3">
        <ConversationMicButton
          isRecording={isRecording}
          isProcessing={isProcessing}
          disabled={inputDisabled}
          onPointerDown={beginRecording}
          onPointerUp={stopRecording}
        />
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("agent.conversation.inputPlaceholder", { targetLanguage })}
          disabled={inputDisabled}
          className="inset-field flex-1 resize-none py-2 text-sm"
          style={{ maxHeight: "80px", overflowY: "auto", borderRadius: "12px" }}
        />
        <SendButton onClick={handleSend} disabled={!input.trim() || inputDisabled} />
      </div>
    </div>
  );
}
